
import { defineComponent, PropType } from 'vue'
import { SALES_URL, TRIAL_PERIOD_DAYS } from '~/utils/billing'

interface ChecklistItem {
  icon: string
  label: string
  action: 'members' | 'slack' | 'slack-open' | 'support'
  url?: string
}

interface SlackConnectChannel {
  status: string
  channelName: string | null
}

export default defineComponent({
  name: 'TrialCard',
  props: {
    daysRemaining: {
      type: Number,
      required: true
    },
    totalTrialDays: {
      type: Number,
      default: TRIAL_PERIOD_DAYS
    },
    checklistItems: {
      type: Array as PropType<ChecklistItem[]>,
      default: () => [
        { icon: 'users', label: 'Invite colleagues', action: 'members' as const },
        { icon: 'slack', label: 'Join Slack channel', action: 'slack' as const },
        { icon: 'headset', label: 'Schedule walkthrough', action: 'support' as const }
      ]
    },
    provider: {
      type: String,
      required: true
    },
    owner: {
      type: String,
      required: true
    },
    slackConnectChannel: {
      type: Object as PropType<SlackConnectChannel | null>,
      default: null
    },
    canInviteMembers: {
      type: Boolean,
      default: true
    },
    showSlackConnect: {
      type: Boolean,
      default: true
    },
    walkthroughUrl: {
      type: String,
      default: SALES_URL
    }
  },
  computed: {
    filteredChecklistItems(): ChecklistItem[] {
      const status = this.slackConnectChannel?.status ?? null
      const channelName = this.slackConnectChannel?.channelName ?? null

      const items = this.checklistItems.map((item): ChecklistItem => {
        if (item.action !== 'slack') return item

        if (status === 'CONNECTED') {
          return {
            ...item,
            label: 'Go to Slack',
            action: 'slack-open',
            url: channelName
              ? `https://slack.com/app_redirect?channel=${channelName}`
              : 'https://slack.com'
          }
        }
        return item
      })

      return items.filter(
        (item) =>
          !(
            (!this.canInviteMembers && item.action === 'members') ||
            (!this.showSlackConnect && (item.action === 'slack' || item.action === 'slack-open'))
          )
      )
    },
    isExpired(): boolean {
      return this.daysRemaining <= 0
    },
    isEndingImminently(): boolean {
      return this.daysRemaining <= 1
    },
    isEndingSoon(): boolean {
      return !this.isEndingImminently && this.daysRemaining >= 2 && this.daysRemaining <= 7
    },
    badgeText(): string {
      if (this.isExpired) {
        return 'Ended'
      }
      return `${this.daysRemaining} ${this.daysRemaining === 1 ? 'day' : 'days'} left`
    },
    badgeClasses(): string {
      if (this.isEndingImminently) {
        return 'bg-cherry-500/15'
      }
      if (this.isEndingSoon) {
        return 'bg-honey-500/15'
      }
      return 'bg-vanilla-400/20'
    },
    badgeTextClasses(): string {
      if (this.isEndingImminently) {
        return 'text-cherry-500'
      }
      if (this.isEndingSoon) {
        return 'text-honey-500'
      }
      return 'text-vanilla-300'
    },
    badgeAriaLabel(): string {
      if (this.isExpired) {
        return 'Trial has ended'
      }
      if (this.daysRemaining <= 1) {
        return 'Trial ending soon - 1 day left'
      }
      if (this.daysRemaining <= 7) {
        return `Trial ending soon - ${this.daysRemaining} days left`
      }
      return `Trial active - ${this.daysRemaining} days remaining`
    },
    remainingPercentage(): number {
      return Math.round((this.daysRemaining / this.totalTrialDays) * 100)
    },
    progressBarClasses(): string {
      if (this.remainingPercentage > 50) {
        return 'bg-robin-500'
      }
      if (this.remainingPercentage >= 25) {
        return 'bg-honey-500'
      }
      return 'bg-cherry-500'
    },
    activeButtonClasses(): string {
      if (this.isEndingSoon || this.isEndingImminently) {
        return 'bg-juniper text-ink-400 hover:bg-juniper-600'
      }
      return 'bg-ink-300 border-[0.5px] border-ink-50 text-vanilla-400 hover:bg-ink-200'
    },
    activeButtonIconColor(): string {
      if (this.isEndingSoon || this.isEndingImminently) {
        return 'ink-400'
      }
      return 'vanilla-400'
    }
  },
  methods: {
    getItemAriaLabel(item: ChecklistItem): string {
      switch (item.action) {
        case 'members':
          return `${item.label} - navigate to team members page`
        case 'slack':
          return `${item.label} - opens invite dialog`
        case 'slack-open':
          return `${item.label} - opens Slack channel in new window`
        case 'support':
          return `${item.label} - opens in new window`
        default:
          return item.label
      }
    },
    handleItemClick(item: ChecklistItem) {
      switch (item.action) {
        case 'members':
          this.$router.push(
            this.$generateRoute(['members'], false, true, this.provider, this.owner)
          )
          break
        case 'slack':
          this.$emit('join-slack-channel')
          break
        case 'slack-open':
          window.open(item.url, '_blank', 'noopener,noreferrer')
          break
        case 'support':
          window.open(this.walkthroughUrl, '_blank', 'noopener,noreferrer')
          break
        default:
          break
      }
    },
    navigateToBilling() {
      const billingRoute = this.$generateRoute(
        ['settings', 'billing'],
        false,
        true,
        this.provider,
        this.owner
      )

      // If already on the billing page, go directly to checkout
      if (this.$route.path === billingRoute) {
        this.$router.push(
          this.$generateRoute(
            ['settings', 'billing', 'checkout'],
            false,
            true,
            this.provider,
            this.owner
          )
        )
      } else {
        this.$router.push(billingRoute)
      }
    }
  }
})
